import { useState, type FormEvent } from 'react';
import { CheckCircle, AlertCircle, Loader2, Upload } from 'lucide-react';
import type { JobApplicationData } from '../types';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';

interface JobApplicationFormProps {
  jobId: string;
  jobTitle: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function JobApplicationForm({ jobId, jobTitle, onClose, onSuccess }: JobApplicationFormProps) {
  const [formData, setFormData] = useState<JobApplicationData & { fileLink: string; botcheck: string }>({
    name: '',
    email: '',
    phone: '',
    jobId,
    message: '',
    fileLink: '',
    botcheck: '',
  });
  
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const accessKey = import.meta.env.VITE_WEB3FORMS_KEY;
    
    if (!accessKey) {
      setStatus('error');
      setErrorMessage('Konfigurationsfehler: Web3Forms Key nicht gefunden.');
      return;
    }

    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `Bewerbung: ${jobTitle}`,
          from_name: 'ConciergeUnit Jobs',
          to: 'info@conciergeunit.de',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          job_id: jobId,
          job_title: jobTitle,
          message: formData.message,
          file_link: formData.fileLink,
          botcheck: formData.botcheck,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 3000);
      } else {
        throw new Error(data.message || 'Ein Fehler ist aufgetreten');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" role="alert" aria-live="polite">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" aria-hidden="true" />
        <h3 className="text-xl font-bold text-green-800 mb-2">Bewerbung erfolgreich!</h3>
        <p className="text-green-700">
          Vielen Dank für Ihre Bewerbung. Wir werden diese sorgfältig prüfen und uns innerhalb von 7 Werktagen bei Ihnen melden.
        </p>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit} 
      className="space-y-5"
      aria-label={`Bewerbungsformular für ${jobTitle}`}
    >
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" role="alert" aria-live="assertive">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-red-700 text-sm">{errorMessage}</p>
        </div>
      )}

      {/* Honeypot Field */}
      <div className="hidden" aria-hidden="true">
        <input 
          type="text" 
          name="botcheck" 
          tabIndex={-1} 
          autoComplete="off"
          value={formData.botcheck}
          onChange={handleChange}
        />
      </div>

      <div>
        <label htmlFor="job-position" className="block text-sm font-medium text-gray-700 mb-2">
          Position
        </label>
        <input
          type="text"
          id="job-position"
          value={jobTitle}
          disabled
          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-600"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="apply-name" className="block text-sm font-medium text-gray-700 mb-2">
            Vollständiger Name <span className="text-red-500" aria-label="Pflichtfeld">*</span>
          </label>
          <input
            type="text"
            id="apply-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            placeholder="Max Mustermann"
            autoComplete="name"
          />
        </div>

        <div>
          <label htmlFor="apply-email" className="block text-sm font-medium text-gray-700 mb-2">
            E-Mail <span className="text-red-500" aria-label="Pflichtfeld">*</span>
          </label>
          <input
            type="email"
            id="apply-email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
            placeholder="max@email.de"
            autoComplete="email"
          />
        </div>
      </div>

      <div>
        <label htmlFor="apply-phone" className="block text-sm font-medium text-gray-700 mb-2">
          Telefon <span className="text-red-500" aria-label="Pflichtfeld">*</span>
        </label>
        <input
          type="tel"
          id="apply-phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
          placeholder="+49 123 456789"
          autoComplete="tel"
        />
      </div>

      <div>
        <label htmlFor="apply-message" className="block text-sm font-medium text-gray-700 mb-2">
          Warum sind Sie die richtige Person? <span className="text-red-500" aria-label="Pflichtfeld">*</span>
        </label>
        <textarea
          id="apply-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={4}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors resize-none"
          placeholder="Erzählen Sie uns von Ihrer Erfahrung und warum Sie bei ConciergeUnit arbeiten möchten..."
        />
      </div>

      <div>
        <label htmlFor="apply-file" className="block text-sm font-medium text-gray-700 mb-2">
          Link zu Lebenslauf & Anschreiben (optional)
        </label>
        <input
          type="url"
          id="apply-file"
          name="fileLink"
          value={formData.fileLink}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
          placeholder="https://drive.google.com/... oder https://dropbox.com/..."
        />
        <p className="mt-2 text-sm text-gray-500">
          Alternativ können Sie Ihre Unterlagen auch per E-Mail an{' '}
          <a href="mailto:info@conciergeunit.de" className="text-brand-600 hover:underline">
            info@conciergeunit.de
          </a>{' '}
          senden.
        </p>
      </div>

      <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
        <p className="mb-2">
          <strong>Hinweis zum Datenschutz:</strong>
        </p>
        <p>
          Ihre Daten werden gemäß unserer{' '}
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
            Datenschutzerklärung
          </a>{' '}
          verarbeitet. Im Rahmen des Bewerbungsprozesses werden Ihre Daten an den Auftragsverarbeiter Web3Forms übermittelt.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="btn-primary flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          aria-busy={status === 'submitting'}
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              Wird gesendet...
            </>
          ) : (
            <>
              <Upload className="w-5 h-5" aria-hidden="true" />
              Bewerbung absenden
            </>
          )}
        </button>
      </div>
    </form>
  );
}
